import { SECTION_TYPES } from '../values/sections';
import Course from '@/content/models/course.model';
import { COURSE_API } from '@/courses/values/courses.values';
import { CourseService } from '@/courses/services/courses.service'
import {HomeworksService} from '@/homework/services/homeworks.service';
import {PAGE_SIZE, HOMEWORK_STATUS} from '@/homework/values/homeworks.values';
import { RequestProvider } from '@/shared/providers/request.provider';
import { SectionsProvider } from './../providers/sections.provider';
import Vue from 'vue';
import { MUTATIONS } from '@/classes/values/classes.values';

class CourseActions {

  async createCourseMaterialSection(ctx , data): Promise<void> {
    const req = COURSE_API.CREATE_MATERIAL_SECTION.replace('{courseId}', String(data.courseId))
    const item = { name: data.name, parentId: data.parentId };
    const res = await RequestProvider.post(req, item);
    Vue.prototype.$Courses.Store.setCourseMaterialSection({
      section: res, courseId: data.courseId, classId: data.classId
    });
  }

  async updateCourseMaterialSection(ctx, {name, materialSectionId , courseId , classId}): Promise<void> {
    const req = COURSE_API.EDIT_MATERIAL_SECTION
      .replace('{courseId}', String(courseId))
      .replace('{sectionId}', String(materialSectionId));
    const data = new FormData();
    data.append('newName', name);
    await RequestProvider.put(req, data);
    Vue.prototype.$Courses.Store.updateMaterialSectionName({
      name, materialSectionId, courseId, classId
    });
  }

  async moveCourseMaterialSection(ctx ,data): Promise<void> {
    const path = COURSE_API.MOVE_MATERIAL_SECTION
      .replace('{courseId}', String(data.courseId))
      .replace('{materialSectionId}', String(data.materialSectionId));
    const request = new FormData();
    request.append('newMaterialSectionParentId', data.newMaterialSectionParentId);
    request.append('newOrder', data.newOrder);
    return RequestProvider.put(path, request);
  }

  async moveCourseMaterial(ctx , data): Promise<any> {
    const req = COURSE_API.MOVE_COURSE_MATERIAL
      .replace('{courseId}', String(data.courseId))
      .replace('{materialId}', String(data.materialId));
    const request = new FormData();
    request.append('newMaterialSectionId' , data.materialSectionId);
    request.append('newOrder' , data.newOrder);
    return RequestProvider.put(req, request);
  }

  async removeSectionMaterial(ctx , {sectionId , courseId , classId}): Promise<void> {
    const req = COURSE_API.REMOVE_MATERIAL_SECTION
      .replace('{courseId}', String(courseId))
      .replace('{sectionId}', String(sectionId));
    await RequestProvider.remove(req);
    Vue.prototype.$Courses.Store.removeCourseMaterialSection({
      sectionId, courseId, classId
    });
  }

  addCourseMaterial(ctx, {material , courseId , classId}) {
    Vue.prototype.$Courses.Store.setCourseMaterial({
      material, courseId, classId
    });
  }

  async removeCourseMaterial(ctx , data): Promise<void> {
    const req = COURSE_API.REMOVE_COURSE_MATERIAL
      .replace('{courseId}', String(data.courseId))
      .replace('{materialId}', String(data.materialId));
    await RequestProvider.remove(req);
    Vue.prototype.$Courses.Store.removeCourseMaterial({
      materialId: data.materialId,
      relatedCourseMaterials: data.relatedCourseMaterials,
      courseId: data.courseId,
      classId: data.classId
    });
  }

  async updateCourseMaterial(ctx , {data, courseId}) {
    const req = COURSE_API.EDIT_COURSE_MATERIAL
      .replace('{courseId}', String(courseId))
      .replace('{materialId}', String(data.materialId));
    const material: any = {
      name: data.name,
      offset: data.offset,
      saveMessageTeacherOnly: data.saveMessageTeacherOnly,
      component: data.component,
    };

    if(Object.prototype.hasOwnProperty.call(data,'recreate')) {
      material.recreate = data.recreate
    }

    if(Object.prototype.hasOwnProperty.call(data,'teacherOnly')) {
      material.teacherOnly = data.teacherOnly;
    }

    const request = new FormData();

    try {
      const res = await RequestProvider.put(req, material);
      if(data.parentSection && Object.prototype.hasOwnProperty.call(data,'teacherOnly')) {
        data.parentSection.materials = data.parentSection.materials.map(m => {
          if(m.relatedContentId == data.materialId){
            m.data.teacherOnly = data.teacherOnly;
          }
          return m;
        });
      }
      return res;
    } catch(err) {
      throw err.response
    }

  }

  async updateCourseFilesIfInfoReady(ctx , data) {
    const req = COURSE_API.STATE_SECTION_FILES
      .replace('{courseId}', String(data.courseId))
      .replace('{sectionId}', String(data.sectionId))
      .replace('{classId}', data.classId ? String(data.classId) : '');
    const res = await RequestProvider.get(req);
    Vue.prototype.$Courses.Store.updateSection({
      section: res,
      courseId: data.courseId,
      classId: data.classId
    });
    return res;
  }

  async updateCourseMaterialIfInfoReady(ctx , data) {
    const req = COURSE_API.STATE_COURSE_MATERIAL
      .replace('{courseId}' , String(data.courseId))
      .replace('{materialId}' , String(data.materialId));
    const res = await RequestProvider.get(req);
    Vue.prototype.$Courses.Store.updateCourseMaterial({
      material: res,
      courseId: data.courseId,
      classId: data.classId
    })
    return res;
  }

  getCourseInfo(ctx , {courseId, classId = null}) {
    let req = classId ? 
        COURSE_API.GET_COURSE_CLASS_INFO
          .replace('{courseId}', String(courseId))
          .replace('{classId}', String(classId))
      : COURSE_API.GET_COURSE_INFO
          .replace('{courseId}', String(courseId));

    return RequestProvider.get(req)
    .then((res: any) => {
      let schoolCalendar = null;
      if(res.schoolDay) {
        schoolCalendar = {
          schoolDayId: res.schoolDay?.schoolDayId,
          dates: res.schoolDay?.dates
        }
      }
      const course = new Course({
        id: res.contentId,
        type: res.type,
        title: res.contentTitle,
        subtitle: res.categoriesDescription,
        img: res.coverThumbnailSmallUrl,
        defaultDueDateForTasks: res.defaultDueDateForTasks,
        defaultLastDateForTasks: res.defaultLastDateForTasks,
        hasGrading: res.hasGrading,
        origin: res.origin,
        suggestedLessonDuration: res.suggestedLessonDuration,
        builderVersion: res.builderVersion
      });
      Vue.prototype.$Courses.Store.setCourseInfo(course);
    });
  }

  async getCourseEdition(ctx, courseId) {
    const req = COURSE_API.GET_COURSE_EDITION
      .replace('{courseId}', String(courseId));
    const res = await RequestProvider.get(req);
    const course = new Course({
      id: res.contentId,
      type: res.type,
      title: res.contentTitle,
      img: res.coverThumbnailSmallUrl,
      defaultDueDateForTasks: res.defaultDueDateForTasks,
      defaultLastDateForTasks: res.defaultLastDateForTasks,
      contentSummary: res.contentSummary,
      categoryId: res.category.id,
      imgHash: res.coverImg,
      hasGrading: res.hasGrading,
      duration: res.duration,
      schoolCalendar: res.schoolDay,
      origin: res.origin,
      suggestedLessonDuration: res.suggestedLessonDuration,
    });
    return course;
  }

  async getContentByEditor(ctx, {courseId, classId = null}): Promise<void> {
    const req = COURSE_API.GET_CONTENT_BY_EDITOR
      .replace('{courseId}', String(courseId));
    const res = await RequestProvider.get(req);
    Vue.prototype.$Courses.Store.setContentForCourse({
      data: res, courseId, classId
    });
    return;
  }

  async getContentForCourse(ctx, {courseId , classId = null}): Promise<void> {
    const data = await CourseService.getContentForCourse(courseId, classId);
    Vue.prototype.$Courses.Store.setContentForCourse({
      data, courseId, classId
    });
    return;
  }

  async setContentForCourse(ctx, {data, courseId , classId = null}): Promise<void> {
    Vue.prototype.$Courses.Store.setContentForCourse({
      data, courseId, classId
    });
    return;
  }

  getGradingDetailsForCourse(ctx, {courseId , classId = null}): Promise<void> {
    return new Promise((resolve, reject) => {
      CourseService.getGradingDetailsForCourse(courseId, classId).then((data) => {
        Vue.prototype.$Courses.Store.setGradingDetailsForCourse({
          gradingDetails: data, courseId , classId
        });
        resolve();
      }).catch(() => {
        reject();
      });
    });
  }

  removeImgFromSection(ctx, { section, hash }): Promise<any> {
    const req = COURSE_API.REMOVE_IMAGE
      .replace('{courseId}', String(section.courseId))
      .replace('{sectionId}', String(section.sectionId))
      .replace('{hash}', String(hash));
    return RequestProvider.remove(req);
  }

  createSection(ctx, payload) {
    const parentID = payload.parentID ? payload.parentID : '-1';
    const req = COURSE_API.CREATE_SECTION
      .replace('{courseId}', String(payload.courseId))
      .replace('{parentId}', String(parentID));

    payload.text = null;
    payload.homework = false;
    payload.description = payload.description || '';
    const data = new FormData();
    const section = JSON.stringify({
      name: payload.name,
      text: null,
      teacherOnly: false,
      description: payload.description,
      type: payload.type,
      homework: false,
      startSchoolDay: payload.startSchoolDay,
      autoGeneratedName: payload.autoGeneratedName,
      dueDateAuto: true,
      lastDateAuto: true,
      fixedSection: payload.fixedSection,
      lessonDuration: payload.lessonDuration,
    });
    data.append('section', section);
    return new Promise((resolve, reject) => {
      RequestProvider.post(req, data)
      .then((res: any) => {
        payload.id = res.id;
        Vue.prototype.$Courses.Store.createSection({
          section: {
            ...payload,
            parentId: payload.parentID,
            submissionDates: {
              startSchoolDay: payload.startSchoolDay,
              dueDateAuto: true,
              lastDateAuto: true,
              startDate: payload.startDate
            }
          },
          parentID: payload.parentID,
          courseId: payload.courseId,
          classId: payload.classId
        });
        resolve(res.id);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  updateSection(ctx, section): Promise<void> {
    const req = COURSE_API.EDIT_SECTION
      .replace('{courseId}', String(section.courseId));

    return new Promise((resolve, reject) => {

      const body: any = {};
      const submissionDates = section.submissionDates || {
        dueDateAuto: true,
        lastDateAuto: true
      };

      if(section.type !== SECTION_TYPES.LESSON_IMAGES.code) {
        body.text = section.text;
      }

      body.sectionId = section.sectionId;
      body.name = section.name;
      body.description = section.description || '';
      body.type = section.type;
      body.homework = section.homework;
      body.teacherOnly = section.teacherOnly || false;
      body.autoGeneratedName = section.autoGeneratedName || false;
      body.homeworkPoints = section.homeworkPoints || 0;
      body.homeworkWeight = section.homeworkWeight || 0;
      body.dueSchoolDay = submissionDates.dueSchoolDay || '';
      body.startSchoolDay = submissionDates.startSchoolDay || '';
      body.lastSchoolDay = submissionDates.lastSchoolDay || '';
      body.dueDateAuto = submissionDates.dueDateAuto;
      body.lastDateAuto = submissionDates.lastDateAuto;
      body.recalculateDueDate = section.recalculateDueDate || false;
      body.recalculateLastDate = section.recalculateLastDate || false;
      body.archiveCurrentHomeworks = section.archiveCurrentHomeworks || false;
      body.fixedSection = section.fixedSection;
      body.labelSections = section.labelSections;
      body.saveAsPdf = section.saveAsPdf;
      body.rotatePdfInfo = section.rotatePdfInfo;
      body.component = section.component;
      body.lessonDuration = section.lessonDuration || '';

      RequestProvider.put(req , body)
        .then(() => {
          let data = { section, courseId: section.courseId, classId: section.classId };
          Vue.prototype.$Courses.Store.updateSection(data);
          resolve();
        }).catch((err) => {
          reject(err.response);
        });
    });
  }

  reorderSection(ctx, { section, parentId, place, courseId, options }): Promise<void> {
    let req = COURSE_API.REORDER_SECTION.replace('{courseId}', String(courseId));
    req = parentId ? `${req}/${parentId}/${place}` : `${req}/${place}`
    const data: any = {};
    if(section.sectionId) {
      data.sectionId = section.sectionId;
    } else {
      data.sectionId = section.id;
    }
    if(options) {
      data.recalculateDueDate = options.recalculateDueDate;
      data.recalculateLastDate = options.recalculateLastDate;
    }
    return RequestProvider.post(req, data);
  }

  async removeFile(ctx, section): Promise<void> {
    const req = COURSE_API.REMOVE_FILE
      .replace('{courseId}', String(section.courseId))
      .replace('{sectionId}', String(section.sectionId))
      .replace('{sectionText}', String(section.text));
    await RequestProvider.remove(req);
    section.text = '';
    section.assetsInfo = null;
    let data = {
      section,
      courseId: section.courseId,
      classId: section.classId
    };
    Vue.prototype.$Courses.Store.updateSection(data);
  }

  removeSection(ctx, {sectionId, courseId , classId}): Promise<void> {
    const req = COURSE_API.REMOVE_SECTION
      .replace('{courseId}', String(courseId))
      .replace('{sectionId}', String(sectionId));
    return new Promise((resolve, reject) => {
      RequestProvider.remove(req)
      .then(() => {
        Vue.prototype.$Courses.Store.removeSection({
          sectionId,
          courseId,
          classId
        });
        resolve();
      }).catch((err) => {
        reject(err);
      });
    })
  }

  /* Subject projects */
  createProject(ctx, payload) {
    const projectParentId = payload.parentId ? payload.parentId : null;
    const req = COURSE_API.CREATE_PROJECT;
    const data = {
      contentId: payload.courseId,
      name: payload.name,
      projectParentId: projectParentId,
      type: payload.type
    }
    return new Promise((resolve, reject) => {
      RequestProvider.post(req, data)
      .then((res: any) => {
        payload.projectId = res.projectId;
        Vue.prototype.$Courses.Store.createProject({
          project: {
            ...payload,
            contentId: payload.courseId
          },
          parentId: payload.parentId,
          courseId: payload.courseId,
          classId: payload.classId,
        });
        resolve(res.projectId);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  updateProject(ctx, payload): Promise<void> {
    const req = COURSE_API.UPDATE_PROJECT
      .replace('{projectId}', String(payload.projectId));
    return new Promise((resolve, reject) => {
      const data = {
        contentId: payload.contentId,
        name: payload.name,
        projectId: payload.projectId
      }
      RequestProvider.put(req , data)
        .then(() => {
          const response = {
            project: payload,
            courseId: payload.contentId,
            classId: payload.classId
          };
          Vue.prototype.$Courses.Store.updateProject(response);
          resolve();
        }).catch((err) => {
          reject(err.response);
        });
    });
  }

  removeProject(ctx, {projectId, courseId, classId}): Promise<void> {
    const req = COURSE_API.REMOVE_PROJECT
      .replace('{courseId}', String(courseId))
      .replace('{projectId}', String(projectId));
    return new Promise((resolve, reject) => {
      RequestProvider.remove(req)
      .then(() => {
        Vue.prototype.$Courses.Store.removeProject({
          projectId, courseId , classId
        });
        resolve();
      }).catch((err) => {
        reject(err);
      });
    })
  }

  reorderProject(ctx, { project, parentId, place, courseId }): Promise<void> {
    const req = COURSE_API.REORDER_PROJECT.replace('{projectAfterId}' , String(place));
    return RequestProvider.post(req, {
      contentId: courseId,
      projectId: project.projectId,
      projectParentId: parentId,
    });
  }

  saveProjectFiles(ctx, { parentId, courseId, classId, addSections, removeSections }): Promise<void> {
    const req = COURSE_API.SAVE_PROJECT_FILES
    const data = {
      contentId: courseId,
      projectParentId: parentId,
      name: 'Section',
      sections: addSections,
      sectionsToRemove: removeSections,
      type: 'SECTION'
    }
    return new Promise((resolve, reject) => {
      RequestProvider.post(req, data)
      .then((sectionsAdded: any) => {
        Vue.prototype.$Courses.Store.saveProjectFiles({
          projectId: parentId, sectionsAdded, courseId, classId, removeSections
        })
        resolve();
      }).catch((err) => {
        reject(err);
      });
    });
  }

  getCourses(ctx, { classId }): Promise<void> {
    return new Promise((resolve, reject) => {
      CourseService.getClassContents(
        ctx.state.courses.page,
        ctx.state.courses.pageSize
      ).then((res: any) => {
        if(classId) {
          res.content = res.content.filter(c => c.classId === classId);
        }
        Vue.prototype.$Courses.Store.setCourses(res.content)
        resolve();
      }).catch(() => {
        reject();
      });
    });
  }

  createSectionFromObject(ctx, payload) {
    const req = COURSE_API.CREATE_SECTION_FROM_OBJECT
      .replace('{courseId}', String(payload.courseId))
      .replace('{parentId}', String(payload.parentId));

    const data = new FormData();
    const section = {
      name: payload.name,
      text: payload.text,
      description: payload.description,
      type: payload.type,
      homework: payload.homework,
      homeworkPoints: payload.homeworkPoints,
      homeworkWeight: payload.homeworkWeight,
      ...payload.submissionDates,
      saveAsPdf: payload.saveAsPdf
    };

    if (payload.labelSections && payload.labelSections !== '') {
      section.labelSections = payload.labelSections;
    }

    if (payload.component) {
      section.component = payload.component;
    }

    data.append('section', JSON.stringify(section));
    if(payload.file) {
      data.append('file', payload.file);
    }

    if('sectionMaterial' in payload) {
      data.append('sectionMaterial', payload.sectionMaterial);
    }

    return RequestProvider.post(req, data);
  }

  copySection(ctx, {sectionID, parentID, moveSection, courseId, classId}) {
    return new Promise((resolve, reject) => {
      RequestProvider.post(
        `content/${courseId}/structure/${sectionID}/copy`
      ).then((section: any) => {
        let data = { section, parentID , courseId , classId};
        if(moveSection) {
          Vue.prototype.$Courses.Store.createSection({
            ...data, copiedSectionId: sectionID
          });
        } else {
          Vue.prototype.$Courses.Store.createSection(data);
        }
        resolve(section);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  async rotateContentAsset(ctx, payload:any) : Promise<string> {

    if( (payload.rotatePageList || []).length <= 0 ) {
      return null;
    }

    const data = {
      sectionId: payload.sectionId,
      rotatePageList: payload.rotatePageList
    };

    const url = `content/${payload.courseId}/structure/asset/rotate`;
    const hashAssetRotated = await RequestProvider.post(url, data);
    return hashAssetRotated;
    //Vue.prototype.$Courses.Store.createSection({...data, copiedSectionId: sectionID});
  }

  updateSectionMaterial(ctx, payload) {
    const req = COURSE_API.EDIT_COURSE_MATERIAL_OBJECT
      .replace('{courseId}' , String(payload.courseId));
    return RequestProvider.put(req, payload);
  }

  updateSectionMaterialVisibility(ctx, {sectionId, teacherOnly, courseId, saveMessageTeacherOnly}) {
    let req = COURSE_API.UPDATE_SECTION_MATERIAL_VISIBILITY
      .replace('{courseId}', String(courseId))
      .replace('{sectionId}', String(sectionId))
      .replace('{teacherOnly}', String(teacherOnly))
      .replace('{saveMessageTeacherOnly}', String(saveMessageTeacherOnly));
    return RequestProvider.put(req, { teacherOnly });
  }

  createWriteObject(ctx, { payload, courseId, parentId }) {
    return RequestProvider.post(
      `content/upload/${courseId}/section/${parentId}/object/split`,
      payload,
    );
  }

  updateWriteObject(ctx, {payload, courseId, parentId, sectionId}) {
    return RequestProvider.post(
      `content/upload/${courseId}/section/${parentId}/object/${sectionId}/split`,
      payload,
    );
  }

  async updateFile(ctx, payload) {
    const data = new FormData();
    if(payload.sectionType) {
      data.append('section', JSON.stringify({
        type: payload.sectionType
      }));
    }
    data.append('file', payload.file);
    return RequestProvider.post(
      `content/upload/${payload.courseId}/structure/${payload.sectionId}`,
      data,
    );
  }

  setTeacherOnlySection(ctx, { sectionId, courseId, teacherOnly , classId }): Promise<void> {
    return new Promise((resolve, reject) => {
      CourseService.setTeacherOnlySection(
        courseId, sectionId, teacherOnly
      ).then(() => {
        Vue.prototype.$Courses.Store.setTeacherOnlySection({
          sectionId, teacherOnly , courseId , classId
        });
        resolve();
      }).catch(() => {
        reject();
      });
    });
  }

  getPresentationCourses(ctx, {classId}): Promise<void> {
    return new Promise((resolve, reject) => {
      CourseService.obtainCoursesByClass(classId)
      .then((response) => {
        Vue.prototype.$Courses.Store.setPresentationCourses({
          coursesList: response, classId
        });
        resolve();
      }).catch(() => {
        reject();
      });
    });
  }

  getCompletedTaskByStudent(ctx, { sectionId , courseId , classId, page = 0 }) {
    return new Promise((resolve, reject) => {
      HomeworksService.getSectionsHomeworksFromTeacherWidget({
        classId: classId,
        contentId: courseId,
        sectionId: sectionId,
        page: page,
        pageSize: PAGE_SIZE,
        includeArchived: true,
        status: [HOMEWORK_STATUS.GRADED, HOMEWORK_STATUS.FINISHED]
      }).then((res: any) => {
        Vue.prototype.$Courses.Store.setCompletedTaskBySection({
          persons: res,
          section: { sectionId, courseId, classId},
          isReload: false
        });
        resolve(res);
      }).catch(err => {
        reject(err);
      });
    });

  }

  createDeck(ctx, payload) {
    const req = COURSE_API.CREATE_DECK.replace('{contentId}', String(payload.contentId));
      const createRequest = {
        deckName: payload.deckName,
        cardType: payload.cardType,
        componentId: payload.component?.id
      };
      return new Promise((resolve, reject) => {
        RequestProvider.post(req, createRequest).then((res: any) => {
          payload.id = res.id;
          payload.fixedSection = res.fixedSection;
          payload.type = res.type;
          payload.name = res.name;
          Vue.prototype.$Courses.Store.createDeck({
            deckSection: { ...payload},
            courseId: payload.contentId,
            classId: payload.classId,
          });
        resolve(res.id);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  updateDeck(ctx, payload) {
    const req = COURSE_API.UPDATE_DECK
      .replace('{contentId}', String(payload.contentId))
      .replace('{deckId}', String(payload.deckSectionId));
      const updateRequest = {
        deckName: payload.deckName,
        componentId: payload.component?.id
      };
      return new Promise((resolve, reject) => {
        RequestProvider.put(req, updateRequest).then((res: any) => {
          payload.id = res.id;
          Vue.prototype.$Courses.Store.updateDeck({
            deckSection: res,
            courseId: payload.contentId,
            classId: payload.classId,
        });
        resolve(res.id);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  removeDeck(ctx, {deckSectionId, courseId , classId}): Promise<void> {
    const req = COURSE_API.REMOVE_DECK
      .replace('{courseId}', String(courseId))
      .replace('{deckId}', String(deckSectionId));
    return new Promise((resolve, reject) => {
      RequestProvider.remove(req)
      .then(() => {
        resolve();
      }).catch((err) => {
        reject(err);
      });
    })
  }

  refreshDeck(ctx, {sectionId, courseId , classId}): Promise<void> {
    return new Promise((resolve) => {
      CourseService.obtainSectionTree(courseId, sectionId)
        .then(async response => {
          let deck = SectionsProvider.setCourseContent({ parentSections: [response], sections: [response], courseId: courseId,  classId: classId })[0];
          deck.sections = SectionsProvider.setCourseContent({ parentSections: response.sections, sections: response.sections, courseId: courseId,  classId: classId });

          Vue.prototype.$Courses.Store.refreshDeck({
            deckSection: deck,
            courseId,
            classId
          });
          resolve();
        });
    });
  }

  refreshSectionStructure(ctx, {sectionId, courseId , classId}): Promise<void> {
    return new Promise((resolve) => {
      CourseService.obtainSectionTree(courseId, sectionId)
        .then(async response => {
          let section = SectionsProvider.setCourseContent({ parentSections:  [response], sections: [response], courseId: courseId,  classId: classId })[0];
          section.sections = SectionsProvider.setCourseContent({ parentSections: response.sections, sections: response.sections, courseId: courseId,  classId: classId });

          Vue.prototype.$Courses.Store.updateSection({
            section,
            courseId,
            classId
          });
          resolve();
        });
    });
  }

  createCard(ctx, payload) {
    const data = new FormData();
    const card: any = {};
    const req = COURSE_API.CREATE_CARD
      .replace('{contentId}', String(payload.contentId))
      .replace('{deckId}', String(payload.deckSectionId));

    if (payload.frontFile) data.append('frontFile', payload.frontFile);
    if (payload.backFile) data.append('backFile', payload.backFile);
    if (payload.frontSource) card.frontSource = JSON.stringify(payload.frontSource);
    if (payload.backSource) card.backSource = JSON.stringify(payload.backSource);
    card.name = payload.name;

    data.append('cardRequest',  JSON.stringify(card));

    return new Promise((resolve, reject) => {
      RequestProvider.post(req, data, '', { 'X-API-VERSION': 2 }).then((res: any) => {
        payload.id = res.id;
        let card = SectionsProvider.setCourseContent({ parentSections: [res], sections: [res], courseId: payload.contentId,  classId: payload.classId })[0];
        card.sections = SectionsProvider.setCourseContent({ parentSections: res.sections, sections: res.sections, courseId: payload.contentId,  classId: payload.classId });
        Vue.prototype.$Courses.Store.createCard({
          cardSection: card,
          courseId: payload.contentId,
          classId: payload.classId,
          deckSectionId: payload.deckSectionId,
        });
        resolve(res.id);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  updateCard(ctx, payload) {
    const data = new FormData();
    const card: any = {};
    const req = COURSE_API.UPDATE_CARD
      .replace('{contentId}', String(payload.contentId))
      .replace('{cardId}', String(payload.cardSectionId));

    if (payload.frontFile) data.append('frontFile', payload.frontFile);
    if (payload.backFile) data.append('backFile', payload.backFile);
    if (payload.frontSource) card.frontSource = JSON.stringify(payload.frontSource);
    if (payload.backSource) card.backSource = JSON.stringify(payload.backSource);

    card.name = payload.name;

    data.append('cardRequest',  JSON.stringify(card));

    return new Promise((resolve, reject) => {
      RequestProvider.put(req, data).then((res: any) => {
        payload.id = res.id;
        let card = SectionsProvider.setCourseContent({ parentSections: [res], sections: [res], courseId: payload.contentId,  classId: payload.classId })[0];
        card.sections = SectionsProvider.setCourseContent({ parentSections: res.sections, sections: res.sections, courseId: payload.contentId,  classId: payload.classId });
        Vue.prototype.$Courses.Store.updateCard({
          cardSection: card,
          courseId: payload.contentId,
          classId: payload.classId,
          deckSectionId: payload.deckSectionId,
        });
      resolve(res.id);
    }).catch((err) => {
      reject(err);
    });
  });
  }

  moveCard(ctx, payload) :Promise<void> {
    const req = COURSE_API.MOVE_CARD
    .replace('{contentId}', String(payload.contentId))
    .replace('{cardId}', String(payload.cardSectionId));

    const moveRequest = {
      oldCardOrder: payload.oldCardOrder,
      newCardOrder: payload.newCardOrder
    };
    return new Promise((resolve, reject) => {
      RequestProvider.post(req, moveRequest).then((res: any) => {
        let cards = SectionsProvider.setCourseContent({ parentSections: res, sections: res, courseId: payload.contentId,  classId: payload.classId });
        Vue.prototype.$Courses.Store.refreshDeck({
          deckSection: {
            id: payload.deckSectionId,
            sections: cards},
          courseId: payload.contentId,
          classId: payload.classId
        });
        resolve();
      }).catch((err) => {
        reject(err);
      });
    });
  }


  removeCard(ctx, {cardSectionId, deckSectionId, courseId , classId}) :Promise<void> {
    const req = COURSE_API.REMOVE_CARD
      .replace('{courseId}', String(courseId))
      .replace('{cardId}', String(cardSectionId));
    return new Promise((resolve, reject) => {
      RequestProvider.remove(req)
      .then(() => {
        Vue.prototype.$Courses.Store.removeCard({
          cardSectionId,
          deckSectionId,
          courseId,
          classId
        });
        resolve();
      }).catch((err) => {
        reject(err);
      });
    })
  }

  createSubDeck(ctx, payload) {
    const req = COURSE_API.CREATE_SUB_DECK.replace('{contentId}', String(payload.contentId));
      const createRequest = {
        parentId: payload.parentID,
        deckId: payload.deckId,
        name: payload.name,
        selectMode: payload.selectMode,
        userViewType: payload.userViewType,
        cards: payload.cards,
        labelSection: payload.labelSections
      };
      return new Promise((resolve, reject) => {
        RequestProvider.post(req, createRequest).then((res: any) => {
          /* Deprecated: mutation -> refresh tree after creation */
          Vue.prototype.$Courses.Store.createSection({
            section: {
              ...payload,
              id: res.id,
              type: res.type,
              text: res.text,
              parentId: payload.parentID,
              submissionDates: {
                startSchoolDay: payload.startSchoolDay,
                dueDateAuto: true,
                lastDateAuto: true,
                startDate: payload.startDate
              }
            },
            parentID: payload.parentID,
            courseId: payload.contentId,
            classId: payload.classId
          });
          resolve(res.id);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  updateSubDeck(ctx, payload) {
    let subDeck = payload.subDeck;
    const req = COURSE_API.UPDATE_SUB_DECK
      .replace('{contentId}', String(payload.contentId))
      .replace('{subDeckId}', String(subDeck.id));
      const updateRequest = {
        name: payload.name,
        userViewType: payload.userViewType,
        labelSection: payload.labelSections,
        selectMode: payload.selectMode,
        cards: payload.cards,
      };
      return new Promise((resolve, reject) => {
        RequestProvider.put(req, updateRequest).then((res: any) => {
          subDeck.name = res.name;
          subDeck.text = res.text;
          /* Deprecated: mutation -> refresh tree after update */
          Vue.prototype.$Courses.Store.updateSection({
            section: subDeck,
            courseId: payload.contentId,
            classId: payload.classId
          });
        resolve(res.id);
      }).catch((err) => {
        reject(err);
      });
    });
  }

}

export default new CourseActions();
